import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useLiveQuery } from 'dexie-react-hooks'
import { banco } from '../../db/banco'
import { buscarVivo } from '../../db/registroVivo'
import { agoraISO, novoRegistroBase } from '../../db/tipos'
import { apagarLocalEEnfileirar, gravarEmLote } from '../../sync/outbox'
import CartaoErro from '../../componentes/CartaoErro'
import { confirmar } from '../../componentes/confirmar'
import { registrarGuardaDeSaida, aoClicarComGuarda } from '../../componentes/guardaDeSaida'
import { destinoDeCorrigir } from '../../produtos/corrigirProduto'
import { textoComissaoDaTabela } from '../../comissoes/faixaResumo'
import {
  quadroInicial, regraPropriaCopiandoFornecedora, valoresDaLinha, produtosDoFiltro, haSemComissao,
  errosDaTabela, temAlteracao, gravacoesDaTabela, avisosAoSalvar, desfazerAvisados, linhaIncompleta, faltasDaLinha, precoAnteriorDaLinha, CHAVE_QUADRO,
  type EdicoesDaTabela, type FiltroDaTabela, type QuadroDaTabela as Quadro, type SalvoDaTabela,
} from '../../precos/rascunhoDaTabela'
import LinhaDaTabela from './LinhaDaTabela'
import QuadroDaTabela from './QuadroDaTabela'

const POR_VEZ = 50
const DESCARTAR = 'Você tem alterações não salvas nesta tabela. Descartar?'

export default function EdicaoTabelaPreco() {
  const { id } = useParams()
  const [params] = useSearchParams()
  const navegar = useNavigate()
  const novoId = params.get('novo')
  const [filtro, setFiltro] = useState<FiltroDaTabela>(params.get('filtro') === 'fora' ? 'fora' : 'nesta')
  const [busca, setBusca] = useState('')
  const [mostrar, setMostrar] = useState(POR_VEZ)
  const [edicoes, setEdicoes] = useState<EdicoesDaTabela>({})
  const [quadro, setQuadro] = useState<Quadro | null>(null)
  const [marcados, setMarcados] = useState<Set<string>>(new Set())
  const [erros, setErros] = useState<Record<string, string>>({})
  const [aviso, setAviso] = useState<string | null>(null)
  const [falha, setFalha] = useState<string | null>(null)
  const enviando = useRef(false)

  // Tudo o que esta salvo, numa consulta so (SalvoDaTabela da Task 1).
  const salvo = useLiveQuery(async (): Promise<SalvoDaTabela | null> => {
    const tabela = await buscarVivo(banco.tabelas_preco, id)
    if (!tabela) return null
    const rid = tabela.representada_id
    const regras = await banco.regras_comissao.where('representada_id').equals(rid).filter((r) => r.apagado_em === null).toArray()
    const ids = new Set(regras.map((r) => r.id))
    const percentuais = await banco.comissao_percentuais.filter((p) => p.apagado_em === null && ids.has(p.regra_id)).toArray()
    const precos = await banco.precos.where('tabela_preco_id').equals(tabela.id).filter((p) => p.apagado_em === null).toArray()
    const produtos = await banco.produtos.where('representada_id').equals(rid).filter((p) => p.apagado_em === null).toArray()
    return { tabela, regras, percentuais, precos, produtos }
  }, [id])
  const condicoes = useLiveQuery(
    () => (salvo ? banco.condicoes_pagamento.where('representada_id').equals(salvo.tabela.representada_id).filter((c) => c.apagado_em === null).sortBy('ordem') : []),
    [salvo?.tabela.representada_id], [],
  )

  // O quadro nasce do salvo UMA vez, e renasce depois de salvar -- mas so
  // quando a consulta ja trouxe o salvo NOVO (o de antes do Salvar recriaria o
  // quadro velho, e uma regra recem-criada pareceria "alteracao pendente").
  const salvoAntes = useRef<SalvoDaTabela | null | undefined>(undefined)
  const [recarregar, setRecarregar] = useState(false)
  useEffect(() => {
    if (!salvo) return
    if (quadro === null) setQuadro(quadroInicial(salvo))
    else if (recarregar && salvo !== salvoAntes.current) { setQuadro(quadroInicial(salvo)); setRecarregar(false) }
  }, [salvo, quadro, recarregar])
  // Tabela vazia abre em "Fora da tabela" (spec 2.3).
  const vazia = salvo !== undefined && salvo !== null && salvo.precos.length === 0
  useEffect(() => { if (vazia) setFiltro('fora') }, [vazia])

  const sujo = !!salvo && !!quadro && temAlteracao(salvo, quadro, edicoes)
  const sujoRef = useRef(sujo); sujoRef.current = sujo
  useEffect(() => registrarGuardaDeSaida(async () => !sujoRef.current || confirmar(DESCARTAR, { botao: 'Descartar', perigo: true })), [])

  if (salvo === undefined || (salvo && !quadro)) return <p className="p-4 text-sm text-slate-500">Carregando…</p>
  if (salvo === null) return <p className="p-4 text-sm text-slate-500">Tabela não encontrada.</p>
  const q = quadro!
  const lista = produtosDoFiltro(salvo, q, busca, filtro, novoId)
  const semComissao = haSemComissao(salvo, q)
  const regraForn = salvo.regras.find((r) => r.tabela_preco_id === null)
  const resumoForn = regraForn ? textoComissaoDaTabela({ tipo: regraForn.tipo, pctPadrao: regraForn.pct_padrao, explicitos: [], algumSemExplicito: true }) : 'nenhuma'

  function mudar(produtoId: string, campo: 'preco' | 'avulso' | 'comissao', texto: string) {
    setEdicoes((e) => ({ ...e, [produtoId]: { ...e[produtoId], [campo]: texto } }))
    setAviso(null)
  }

  async function salvar() {
    if (enviando.current || !salvo) return
    const achados = errosDaTabela(salvo, q, edicoes)
    setErros(achados)
    if (Object.keys(achados).length > 0) {
      // linha com erro escondida pelo filtro: troca para onde ela aparece
      const ids = Object.keys(achados).filter((k) => k !== CHAVE_QUADRO)
      if (ids.length > 0 && !lista.some((p) => ids.includes(p.id))) setFiltro(salvo.precos.some((p) => ids.includes(p.produto_id)) ? 'nesta' : 'fora')
      return
    }
    enviando.current = true
    setFalha(null)
    try {
      // Teste dela 29/09 (itens 4 e 5): comissao 0% e preco que muda demais
      // perguntam antes -- pode ser erro de digitacao.
      const avisos = avisosAoSalvar(salvo, q, edicoes)
      if (avisos.length > 0 && !(await confirmar(`Confira antes de salvar:\n\n${avisos.join('\n\n')}`, { botao: 'Salvar assim' }))) {
        setEdicoes(desfazerAvisados(salvo, q, edicoes)) // teste dela 30/09: Cancelar volta o que avisou
        return
      }
      salvoAntes.current = salvo
      const gravacoes = gravacoesDaTabela(salvo, q, edicoes, novoRegistroBase, agoraISO())
      await gravarEmLote(gravacoes)
      // Nada gravado = a consulta nao muda; marcar "recarregar" ficaria
      // pendurado e apagaria um toque seguinte no quadro (revisao final).
      setEdicoes({}); setErros({}); if (gravacoes.length > 0) setRecarregar(true); setAviso('Alterações salvas')
    } catch (e) {
      console.error(e)
      setFalha('Não foi possível salvar. Tente de novo.')
    } finally {
      enviando.current = false
    }
  }

  async function excluirSelecionados() {
    if (!salvo || marcados.size === 0) return
    if (!(await confirmar(`Tirar ${marcados.size} produto(s) desta tabela? Eles continuam no cadastro.`, { botao: 'Tirar', perigo: true }))) return
    for (const produtoId of marcados) {
      const p = salvo.precos.find((x) => x.produto_id === produtoId)
      if (p) await apagarLocalEEnfileirar('precos', p.id)
    }
    setEdicoes((e) => Object.fromEntries(Object.entries(e).filter(([k]) => !marcados.has(k))))
    setMarcados(new Set())
  }

  const destinoProdutoNovo = `/produtos/novo?representada=${salvo.tabela.representada_id}&tabela=${salvo.tabela.id}&voltar=${encodeURIComponent(`/tabelas/${salvo.tabela.id}`)}`
  const naTabela = new Set(salvo.precos.map((p) => p.produto_id))

  return (
    <section className="p-4 pb-28 space-y-3">
      {/* Cabecalho do desenho dela (spec 2.1). O Voltar e o da barra do app,
          que ja pergunta "Descartar?" pela guarda de saida. */}
      <div>
        <h2 className="text-lg font-bold text-slate-800">Editar Tabela de Preços ({salvo.precos.length})</h2>
        <p className="text-xs text-slate-500 break-words">{salvo.tabela.nome}</p>
      </div>

      <QuadroDaTabela representadaId={salvo.tabela.representada_id} quadro={q} condicoes={condicoes} resumoFornecedora={resumoForn}
        erro={erros[CHAVE_QUADRO]} aoMudar={setQuadro}
        aoCriarPropria={() => setQuadro({ ...q, regra: regraPropriaCopiandoFornecedora(salvo) })}
        aoUsarFornecedora={() => setQuadro({ ...q, regra: null })} />

      <div className="flex gap-2">
        <input value={busca} onChange={(e) => { setBusca(e.target.value); setMostrar(POR_VEZ) }} placeholder="Buscar por código ou descrição"
          className="min-w-0 flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" />
        <select aria-label="Filtro" value={filtro} onChange={(e) => { setFiltro(e.target.value as FiltroDaTabela); setMostrar(POR_VEZ); setMarcados(new Set()) }}
          className="border border-slate-300 rounded-lg px-2 text-sm">
          <option value="nesta">Nesta tabela</option>
          <option value="fora">Fora da tabela</option>
          {semComissao && <option value="sem_comissao">Sem comissão</option>}
        </select>
      </div>
      {/* Fora do menu ⋮ (teste dela 29/09, item 10) */}
      <button type="button" onClick={aoClicarComGuarda(navegar, destinoProdutoNovo)}
        className="w-full rounded-lg border border-teal-700 text-teal-800 py-2 text-sm font-medium">+ Produto novo</button>
      {filtro === 'fora' && <p className="text-xs text-slate-500">Busque pelo código ou descrição e dê um preço para incluir o produto nesta tabela.</p>}

      <ul className="space-y-2">
        {lista.slice(0, mostrar).map((p) => (
          <LinhaDaTabela key={p.id} produto={p} valores={valoresDaLinha(salvo, q, p, edicoes[p.id])}
            anterior={precoAnteriorDaLinha(salvo, p, edicoes[p.id])} podeMarcar={naTabela.has(p.id)} marcada={marcados.has(p.id)} alterada={edicoes[p.id] !== undefined} erro={erros[p.id]}
            incompleta={naTabela.has(p.id) && linhaIncompleta(salvo, q, p)}
            aoCorrigir={aoClicarComGuarda(navegar, destinoDeCorrigir(p.id, salvo.tabela.id, faltasDaLinha(salvo, q, p)))}
            aoMarcar={() => setMarcados((m) => { const n = new Set(m); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n })}
            aoMudar={(campo, t) => mudar(p.id, campo, t)} />
        ))}
      </ul>
      {lista.length === 0 && <p className="text-sm text-slate-500">{busca.trim() ? 'Nenhum produto encontrado com esse código ou descrição.' : filtro === 'fora' ? 'Busque o produto que quer incluir.' : 'Nenhum produto aqui.'}</p>}
      {lista.length > mostrar && (
        <button type="button" onClick={() => setMostrar((m) => m + POR_VEZ)} className="w-full rounded-lg border border-slate-300 py-2 text-sm">
          Mostrar mais ({lista.length - mostrar})
        </button>
      )}
      {falha && <CartaoErro titulo="Não foi possível salvar">{falha}</CartaoErro>}

      {/* Barra fixa do desenho dela (spec 2.5), acima da navegacao do app */}
      <div className="fixed inset-x-0 bottom-11 z-20 bg-white border-t border-slate-200 p-2 space-y-1">
        {/* Aqui, e nao no fim da lista, para aparecer (teste dela 29/09, item 3) */}
        {Object.keys(erros).length > 0 && <p className="text-sm text-red-700 text-center">{Object.keys(erros).length} item(ns) para corrigir.</p>}
        {aviso && <p role="status" className="text-sm font-medium text-teal-800 text-center">✓ {aviso}</p>}
        <div className="flex gap-2">
        <button type="button" data-testid="botao-excluir-selecionados" disabled={marcados.size === 0} onClick={() => void excluirSelecionados()}
          className="flex-1 rounded-lg bg-red-50 border border-red-200 text-red-700 py-2 text-sm font-medium disabled:opacity-50">
          Excluir selecionados ({marcados.size})
        </button>
        <button type="button" data-testid="botao-salvar-tabela" disabled={!sujo} onClick={() => void salvar()}
          className="flex-1 rounded-lg bg-teal-800 text-white py-2 text-sm font-medium disabled:opacity-50">
          Salvar alterações
        </button>
        </div>
      </div>
    </section>
  )
}
